<script lang="ts">
  interface VideoSummary {
    youtube_id: string
    analyzed: boolean
    analyzing: boolean
    has_transcript: boolean
    entity_count: number
    created_at: string
    updated_at: string
  }

  let videos = $state<VideoSummary[]>([])
  let loading = $state(true)
  let error = $state('')

  $effect(() => {
    fetchVideos()
  })

  $effect(() => {
    const hasAnalyzing = videos.some(v => v.analyzing)
    if (!hasAnalyzing) return

    const interval = setInterval(fetchVideos, 4000)
    return () => clearInterval(interval)
  })

  async function fetchVideos() {
    if (loading) loading = true
    error = ''
    try {
      const response = await fetch('/api/videos', { credentials: 'same-origin' })
      if (response.ok) {
        videos = await response.json()
      } else {
        error = 'Failed to load videos'
      }
    } catch (e) {
      error = 'Network error'
    }
    loading = false
  }

  async function resetVideo(event: MouseEvent, youtubeId: string) {
    event.preventDefault()
    event.stopPropagation()
    if (!confirm(`Reset analysis for ${youtubeId}? Extracted entities, events and overview are wiped. The transcript stays.`)) return
    try {
      const response = await fetch(`/api/videos/${youtubeId}/reset`, {
        method: 'DELETE',
        credentials: 'same-origin',
      })
      if (response.ok) {
        await fetchVideos()
      } else {
        error = `Failed to reset ${youtubeId} (${response.status})`
      }
    } catch (e) {
      error = 'Network error'
    }
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString)
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  function thumbnailUrl(youtubeId: string): string {
    return `https://i.ytimg.com/vi/${youtubeId}/mqdefault.jpg`
  }
</script>

<div class="studio-console">
  <div class="console-header">
    <div class="header-label">
      <div class="label-primary">VIDEO INDEX</div>
      <div class="label-secondary">{videos.length} VIDEOS</div>
    </div>

    <div class="header-right">
      <a href="/" class="back-btn">
        <span class="btn-label">BACK</span>
      </a>
    </div>
  </div>

  {#if loading}
    <div class="status-message">
      <div class="status-indicator pulse"></div>
      <span>LOADING VIDEOS...</span>
    </div>
  {:else if error}
    <div class="status-message error">
      <div class="status-indicator"></div>
      <span>{error.toUpperCase()}</span>
    </div>
  {:else if videos.length === 0}
    <div class="status-message">
      <div class="status-indicator"></div>
      <span>NO VIDEOS FOUND</span>
    </div>
  {:else}
    <div class="video-list">
      {#each videos as video, idx (video.youtube_id)}
        <a
          href="/?v={video.youtube_id}"
          class="video-strip"
          style="animation-delay: {idx * 0.03}s"
        >
          <div class="video-number">V{String(idx + 1).padStart(2, '0')}</div>

          <div class="video-thumbnail">
            <img
              src={thumbnailUrl(video.youtube_id)}
              alt={video.youtube_id}
              loading="lazy"
            />
          </div>

          <div class="video-info">
            <div class="video-id">{video.youtube_id}</div>
            <div class="video-meta">
              <span>{formatDate(video.updated_at)}</span>
              {#if video.entity_count > 0}
                <span class="entity-count">{video.entity_count} entities</span>
              {/if}
            </div>
          </div>

          <div class="level-section">
            {#if video.analyzing}
              <div class="analyzing-badge">
                <span class="analyzing-spinner"></span>
                <span class="analyzing-label">ANALYZING</span>
              </div>
            {/if}

            {#if video.analyzed}
              <div class="analyzed-badge">ANALYZED</div>
              <button class="reset-btn" onclick={(e) => resetVideo(e, video.youtube_id)} title="Wipe analysis (entities, events, overview). Transcript stays.">
                RESET
              </button>
            {:else if !video.analyzing}
              {#if video.has_transcript}
                <div class="unanalyzed-badge">NOT ANALYZED</div>
              {:else}
                <div class="no-transcript-badge">NO TRANSCRIPT</div>
              {/if}
            {/if}
          </div>
        </a>
      {/each}
    </div>
  {/if}
</div>

<style>
  .studio-console {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background:
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(255, 255, 255, 0.015) 2px,
        rgba(255, 255, 255, 0.015) 4px
      ),
      var(--color-surface-0);
    padding: 2rem;
  }

  .console-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 2.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--color-border-default);
  }

  .header-label {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .label-primary {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--color-text-primary);
    font-family: var(--font-mono);
  }

  .label-secondary {
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 1.25rem;
  }

  .back-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1.25rem;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-strong);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    cursor: pointer;
    transition: all 0.1s;
    text-decoration: none;
  }

  .back-btn:hover {
    background: var(--color-surface-4);
    border-color: var(--color-accent-primary);
  }

  .status-message {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 2rem;
    font-family: var(--font-mono);
    font-size: 0.875rem;
    letter-spacing: 0.1em;
    color: var(--color-text-secondary);
  }

  .status-message.error {
    color: var(--color-accent-playhead);
  }

  .status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-text-muted);
  }

  .status-indicator.pulse {
    background: var(--color-accent-primary);
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }

  .video-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .video-strip {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    background: var(--color-surface-2);
    border: 1px solid var(--color-border-default);
    text-decoration: none;
    color: inherit;
    animation: slideIn 0.3s ease-out forwards;
    opacity: 0;
    transition: all 0.1s;
  }

  .video-strip:hover {
    background: var(--color-surface-3);
    border-color: var(--color-border-strong);
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .video-number {
    font-family: var(--font-mono);
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
    padding: 0.25rem 0.5rem;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-subtle);
    width: fit-content;
    flex-shrink: 0;
  }

  .video-thumbnail {
    width: 120px;
    height: 68px;
    flex-shrink: 0;
    overflow: hidden;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-subtle);
  }

  .video-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .video-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .video-id {
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--color-text-primary);
    letter-spacing: 0.02em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .video-meta {
    display: flex;
    gap: 1rem;
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    color: var(--color-text-muted);
    letter-spacing: 0.02em;
  }

  .entity-count {
    color: var(--color-text-secondary);
  }

  .level-section {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
  }

  .analyzing-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    background: color-mix(in srgb, var(--color-accent-primary) 10%, transparent);
    border: 1px solid color-mix(in srgb, var(--color-accent-primary) 40%, transparent);
    border-radius: 2px;
  }

  .analyzing-spinner {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 1.5px solid color-mix(in srgb, var(--color-accent-primary) 30%, transparent);
    border-top-color: var(--color-accent-primary);
    animation: spin 0.8s linear infinite;
    flex-shrink: 0;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .analyzing-label {
    font-family: var(--font-mono);
    font-size: 0.5625rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    color: var(--color-accent-primary);
  }

  .analyzed-badge {
    font-family: var(--font-mono);
    font-size: 0.5625rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    padding: 2px 6px;
    background: color-mix(in srgb, #4ade80 15%, transparent);
    border: 1px solid color-mix(in srgb, #4ade80 50%, transparent);
    color: #4ade80;
    border-radius: 2px;
  }

  .reset-btn {
    font-family: var(--font-mono);
    font-size: 0.5625rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    padding: 3px 8px;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-default);
    color: var(--color-text-muted);
    border-radius: 2px;
    cursor: pointer;
    transition: all 0.1s;
  }

  .reset-btn:hover {
    border-color: var(--color-accent-playhead);
    color: var(--color-accent-playhead);
  }

  .unanalyzed-badge {
    font-family: var(--font-mono);
    font-size: 0.5625rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    padding: 2px 6px;
    background: color-mix(in srgb, var(--color-text-muted) 15%, transparent);
    border: 1px solid var(--color-border-default);
    color: var(--color-text-muted);
    border-radius: 2px;
  }

  .no-transcript-badge {
    font-family: var(--font-mono);
    font-size: 0.5625rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    padding: 2px 6px;
    background: color-mix(in srgb, #f97316 15%, transparent);
    border: 1px solid color-mix(in srgb, #f97316 50%, transparent);
    color: #f97316;
    border-radius: 2px;
  }
</style>
